import * as React from "react";
import { IChartDataItemV2 } from "@iliotech/types";

import groupBy from "lodash.groupby";

import moment from "moment";
import { ClickableLegendItem } from "@iliotech/component-library";
import {
  DRILL_DOWNS,
  getBackgroundColor,
  useProfitHistory,
} from "@iliotech/data-wire";
import { PortfolioSnapshotDtoV3 } from "@iliotech/generated-api-v3";
import { AllocationOption } from "@iliotech/data-wire/src/reactHooks/context/StructureGrouping/types";
import { useGetGroupedData } from "./useGetGroupedData";

type UsePnlArgs = {
  grouping: AllocationOption;
  selectedGroup?: { code: string; label: string } | undefined;
  setSelectedGroup?(
    nextGroup: { code: string; label: string } | undefined
  ): void;
  currencyFormatter(value: number | undefined, decimal?: number): string;
  snapshots: PortfolioSnapshotDtoV3[];
  t(s: string): string;
};

export const useSnapshotLegendItems = ({
  grouping,
  selectedGroup,
  setSelectedGroup,
  currencyFormatter,
  snapshots,
  t,
}: UsePnlArgs) => {
  const { closedPositions, openPositions } = React.useMemo(() => {
    const closedPositions: PortfolioSnapshotDtoV3[] = [];
    const openPositions: PortfolioSnapshotDtoV3[] = [];
    snapshots.forEach((snapshot) => {
      if (snapshot.marketValueBase === 0) {
        closedPositions.push(snapshot);
      } else {
        openPositions.push(snapshot);
      }
    });
    return { closedPositions, openPositions };
  }, [snapshots]);

  const { getGroupedData } = useGetGroupedData({
    grouping,
    selectedGroup,
    t,
  });

  const getLegendItems = (hideClosedPositions?: boolean) => {
    const legendItems: ClickableLegendItem[] = [];
    const snapshotsToUse = hideClosedPositions ? openPositions : snapshots;
    const onItemClick = (code: string, label: string) => {
      if (!selectedGroup?.code) {
        setSelectedGroup?.({ code, label });
      } else {
        // reset
        setSelectedGroup?.(undefined);
      }
    };

    const onReset = () => {
      setSelectedGroup?.(undefined);
    };

    let grandTotalValue = 0;
    const groupedData = getGroupedData(snapshotsToUse);

    Object.keys(groupedData).forEach((key) => {
      const groupValue = groupedData[key].reduce(
        (acc, curr) => acc + curr.value,
        0
      );
      legendItems.push({
        label: groupedData[key][0].category || key,
        onClick: () => onItemClick(key, groupedData[key][0].category),
        value: currencyFormatter!(groupValue),
        rawValue: groupValue,
        color: groupedData[key][0].color,
      });

      grandTotalValue = grandTotalValue + groupValue;
    });

    legendItems.push({
      label: "Total",
      onClick: onReset,
      value: currencyFormatter!(grandTotalValue),
      rawValue: grandTotalValue,
      // color: groupedData[key][0].color,
    });

    if (hideClosedPositions) {
      const closedValue = closedPositions.reduce(
        (acc, curr) => acc + curr.pnlBase,
        0
      );
      legendItems.push({
        label: "(excl. closed positions)",
        value: currencyFormatter!(closedValue),
        rawValue: closedValue,
        forceToEnd: true,
      });
    }

    return legendItems;
  };

  return {
    getLegendItems,
  };
};
